import type {
  AccountDetail,
  CashLot as Cash,
  DetailedProposal,
  ObjectiveTerm,
  SleeveWithStatisticsAndCompliance,
} from '@moment/horizon-data-access/proposal'
import type { EquationToken } from '@/portfolio/proposal-builder/objective-function/equation-tokens'
import type {
  AccountStatistics,
  ProposalBuilderState,
  ProposalMetadata,
  Sleeve,
  SleeveCompliance,
  SleeveID,
  SleeveStatistics,
} from '@/portfolio/proposal-builder/types'
import type { ComplianceCheck } from '@/types/api/compliance'
import type { ModelConstraint } from '@/types/api/constraints'
import type { Model, ModelStrategy } from '@/types/api/models'
import type {
  ObjectiveConfig,
  ResolvedObjective,
} from '@/types/api/objective-params'
import type { Holding } from '@/types/api/risk-factor'
import type { Statistics } from '@/types/statistics'

import { Currency } from '@moment/trading-types/reference-data'

import { fiConstraintsFromCustomizations } from '@/components/forms/sleeve-management/adapters/customizations'
import { calculateAvailableAccountCash } from '@/portfolio/helpers/calculate-available-account-cash'
import {
  aggregateLotsByIsin,
  computeHoldings,
  computeHoldingsAfter,
} from '@/portfolio/proposal-builder/data/helpers/compute-holdings'
import {
  DEFAULT_CASHFLOW_TARGET_PRIORITY,
  parsePriority,
} from '@/portfolio/proposal-builder/data/helpers/parse-priority'
import { toRebalanceOptions } from '@/portfolio/proposal-builder/data/helpers/rebalance-strategy'
import { generateId } from '@/portfolio/proposal-builder/data/helpers/sleeve-manager'
import { objectiveTreeToTokens } from '@/portfolio/proposal-builder/objective-function/equation-serializer'
import { ProposalMode } from '@/portfolio/proposal-builder/types'

import { consolidateUniverseConstraints } from './consolidate-universe-constraints'
import { initStrategyToRiskFactors } from './constraints-to-risk-factors'
import {
  createEmptyDiversificationParameterConstraints,
  extractDiversificationParameterConstraints,
} from './diversification-parameter-constraints'
import {
  createDefaultGlobalConstraints,
  extractCurrencyFromConstraints,
  filterGlobalConstraints,
  getSleeveCurrency,
  readCurrencyConstraint,
} from './global-constraints'
import {
  extractObjectiveConfig,
  type ObjectiveParamsSource,
} from './objective-config'

function parseInitialAccountCash(
  value: unknown,
): Record<string, number> | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined
  }
  if (Object.values(value).some((v) => typeof v !== 'number')) {
    return undefined
  }
  return value as Record<string, number>
}

// Currency lives on the sleeve, so each lot follows its own sleeve's. Lots with
// no `sleeve_id` predate per-sleeve cash and follow the first sleeve.
function syncCashCurrencyToSleeves(
  accountBefore: AccountDetail | null,
  sleeves: Sleeve[],
): AccountDetail | null {
  const cash = accountBefore?.cash
  if (!accountBefore || !cash?.length || !sleeves.length) return accountBefore

  return {
    ...accountBefore,
    cash: cash.map((lot) => {
      const owner = lot.sleeve_id
        ? sleeves.find((s) => s.sleeveId === lot.sleeve_id)
        : sleeves[0]
      const currency = getSleeveCurrency(owner) ?? lot.currency
      return currency === lot.currency ? lot : { ...lot, currency }
    }),
  }
}

function cashWithSleeveFallback(
  accountBefore: AccountDetail | null,
  sleeves: Sleeve[],
): AccountDetail | null {
  const cash = accountBefore?.cash
  if (!accountBefore || !cash?.length || cash.some((c) => c.sleeve_id)) {
    return accountBefore
  }

  const totalCash = calculateAvailableAccountCash(cash)
  if (totalCash === 0 || sleeves.length === 0) {
    return accountBefore
  }

  const targetTotal = sleeves.reduce(
    (sum, sleeve) => sum + Math.max(sleeve.targetAllocation, 0),
    0,
  )
  const firstCash = cash[0]
  if (!firstCash) {
    return accountBefore
  }

  const sleeveCash: Cash[] = sleeves.map((sleeve) => {
    const allocationShare =
      targetTotal > 0
        ? Math.max(sleeve.targetAllocation, 0) / targetTotal
        : 1 / sleeves.length

    return {
      ...firstCash,
      moment_lot_id: '',
      custodian_lot_id: '',
      moment_reserved_quantity: 0,
      // reset so firstCash's account-level total isn't carried over and over-counted
      open_quantity: 0,
      unsettled_open_quantity: 0,
      settled_open_quantity: totalCash * allocationShare,
      settled_closed_quantity: 0,
      unsettled_closed_quantity: 0,
      sleeve_id: sleeve.sleeveId,
    }
  })

  return { ...accountBefore, cash: sleeveCash }
}

export interface InitialHoldings {
  holdingsBefore: Record<string, Holding[]>
  holdingsAfter: Record<string, Holding[]>
  holdingsHeld: Record<string, Holding[]>
}

// Computes initial holdings for the single active account: keyed by sleeve,
// plus an account-wide aggregation stored under the account id itself
// (sleeve ids are 'slv_'-prefixed, so the keys can't collide).
export function computeInitialHoldings(
  proposal: DetailedProposal,
  accountId: string,
): InitialHoldings {
  const holdingsBefore: Record<string, Holding[]> = {}
  const holdingsAfter: Record<string, Holding[]> = {}
  const holdingsHeld: Record<string, Holding[]> = {}
  const account = proposal.accounts?.[accountId]
  if (!account) {
    return { holdingsBefore, holdingsAfter, holdingsHeld }
  }
  const orders = proposal.orders ?? []
  const beforeLots = account.before?.lots ?? []
  const afterLots = account.after?.lots ?? []
  const heldLots = account.held_lots ?? []
  for (const sleeveId of Object.keys(account.before?.sleeves ?? {})) {
    holdingsBefore[sleeveId] = computeHoldings(beforeLots, sleeveId)
    holdingsHeld[sleeveId] = computeHoldings(heldLots, sleeveId)
    holdingsAfter[sleeveId] = computeHoldingsAfter({
      beforeLots,
      afterLots,
      orders,
      accountId,
      sleeveId,
    })
  }
  // The account's lot lists are already account-wide — aggregate directly,
  // no sleeve filtering needed.
  holdingsBefore[accountId] = aggregateLotsByIsin(beforeLots)
  holdingsAfter[accountId] = computeHoldingsAfter({
    beforeLots,
    afterLots,
    orders,
    accountId,
  })
  holdingsHeld[accountId] = aggregateLotsByIsin(heldLots)
  return { holdingsBefore, holdingsAfter, holdingsHeld }
}

export function extractSleeveStatistics(
  proposal: DetailedProposal,
): Record<SleeveID, SleeveStatistics> {
  const out: Record<SleeveID, SleeveStatistics> = {}
  for (const account of Object.values(proposal.accounts)) {
    const beforeSleeves = account?.before?.sleeves
    if (!beforeSleeves) continue
    for (const [sleeveId, sleeveData] of Object.entries(beforeSleeves)) {
      const afterSleeve = account?.after?.sleeves?.[sleeveId]
      out[sleeveId] = {
        before: (sleeveData?.statistics ?? null) as Statistics | null,
        after: (afterSleeve?.statistics ?? null) as Statistics | null,
      }
    }
  }
  return out
}

// Reads the first account, matching the accountBefore/accountAfter single-account assumption.
export function extractAccountStatistics(
  proposal: DetailedProposal,
): AccountStatistics {
  const firstAccountId = Object.keys(proposal.accounts)[0]
  const account = firstAccountId ? proposal.accounts[firstAccountId] : null
  return {
    before: (account?.before?.statistics ?? null) as Statistics | null,
    after: (account?.after?.statistics ?? null) as Statistics | null,
  }
}

// Pairs each sleeve's `before` and `after` compliance checks. Walks `before` because that's
// the authoritative sleeve set during initial proposal load; the matching `after` sleeve
// (same sleeveId, same account) supplies the post-mutation checks.
export function extractSleeveCompliance(
  proposal: DetailedProposal,
): Record<SleeveID, SleeveCompliance> {
  const out: Record<SleeveID, SleeveCompliance> = {}
  for (const account of Object.values(proposal.accounts)) {
    const beforeSleeves = account?.before?.sleeves
    if (!beforeSleeves) continue
    for (const [sleeveId, sleeveData] of Object.entries(beforeSleeves)) {
      const afterSleeve = account?.after?.sleeves?.[sleeveId]
      out[sleeveId] = {
        before: (sleeveData?.compliance?.checks ?? []) as ComplianceCheck[],
        after: (afterSleeve?.compliance?.checks ?? []) as ComplianceCheck[],
      }
    }
  }
  return out
}

/**
 * Merges model strategy constraints with customizations from model assignment
 *
 * Rules:
 * 1. If customization[id] exists and is not null: REPLACE model constraint
 * 2. If customization[id] is null: REMOVE model constraint
 * 3. If customization[id] has new ID: ADD as new constraint
 *
 * @param strategy - Base model strategy with constraints
 * @param customizations - Customizations from model assignment
 * @param filteredConstraintIds - IDs to filter out (e.g., global constraints)
 * @returns Merged array of constraints
 */
export function mergeStrategyWithCustomizations(
  strategy: ModelStrategy,
  customizations: Record<string, ModelConstraint | null>,
  filteredConstraintIds: Set<string>,
): ModelConstraint[] {
  // Start with model's base constraints
  const baseConstraints = strategy.constraints.filter(
    (constraint) => constraint.id && !filteredConstraintIds.has(constraint.id),
  )

  // Create map of constraint ID -> constraint for easy lookup
  const constraintMap = new Map<string, ModelConstraint>()

  baseConstraints.forEach((constraint) => {
    if (constraint.id) {
      constraintMap.set(constraint.id, constraint)
    }
  })

  // Apply customizations
  Object.entries(customizations).forEach(([id, customization]) => {
    if (customization === null) {
      // Remove constraint
      constraintMap.delete(id)
    } else {
      // Replace or add constraint
      constraintMap.set(id, customization)
    }
  })

  // Return merged constraints
  return Array.from(constraintMap.values())
}

/**
 * Parses API { term: ObjectiveTerm }[] into EquationToken[].
 * Each entry's ObjectiveTerm is deserialized via objectiveTreeToTokens,
 * with + operators interleaved between entries.
 */
function objectiveConfigToTokens(apiTerms: ObjectiveTerm[]): EquationToken[] {
  const allTokens: EquationToken[] = []

  for (const entry of apiTerms) {
    const term = entry.term
    if (!term) continue

    const { tokens } = objectiveTreeToTokens(term)
    if (tokens.length === 0) continue

    if (allTokens.length > 0) {
      allTokens.push({ type: 'operator', op: '+' })
    }
    allTokens.push(...tokens)
  }

  return allTokens
}

// propman returns `resolved_objective` per sleeve on proposal reads, but the
// synced OpenAPI sleeve schema does not carry the field yet. Read it through
// this widened view until the schema sync catches up, then drop it.
type SleeveWithResolvedObjective = SleeveWithStatisticsAndCompliance & {
  resolved_objective?: ResolvedObjective
}

function sleeveResolvedObjective(
  sleeveData: SleeveWithStatisticsAndCompliance,
): ResolvedObjective | undefined {
  return (sleeveData as SleeveWithResolvedObjective).resolved_objective
}

/**
 * Resolves one sleeve's objective tokens.
 *
 * The server-resolved objective wins: propman resolves it per sleeve on every
 * proposal read (model terms, then the org default, then its system default).
 * Model terms remain a transitional fallback for the deploy window where
 * propman does not send resolved_objective yet, and for the model-builder
 * path. defaultTerms exists only because model-builder mode resolves the org
 * default client-side; proposal-mode callers pass no config.
 */
function resolveSleeveObjectiveTokens(args: {
  resolvedObjective: ResolvedObjective | undefined
  model: Model | undefined
  defaultTerms: ObjectiveConfig['buy_objective_terms']
}): EquationToken[] {
  const { resolvedObjective, model, defaultTerms } = args
  const modelTerms =
    extractObjectiveConfig(model as ObjectiveParamsSource | undefined)
      ?.buy_objective_terms ??
    extractObjectiveConfig(
      model?.strategy as unknown as ObjectiveParamsSource | undefined,
    )?.buy_objective_terms
  const terms =
    resolvedObjective?.buy_objective_terms ?? modelTerms ?? defaultTerms
  return terms?.length ? objectiveConfigToTokens(terms) : []
}

/**
 * Initializes ProposalBuilderState from a DetailedProposal
 *
 * Creates sleeves from proposal accounts, merging model strategies with customizations
 *
 * @param proposal - The detailed proposal to initialize from
 * @returns Complete state for ProposalBuilderContext
 */
export function initStateFromProposal(
  proposal: DetailedProposal,
  objectiveConfig?: ObjectiveConfig,
): ProposalBuilderState {
  const sleeves: Sleeve[] = []
  const sleeveCompliance = extractSleeveCompliance(proposal)
  const sleeveStatistics = extractSleeveStatistics(proposal)
  const accountStatistics = extractAccountStatistics(proposal)
  const accountIds = Object.keys(proposal.accounts)

  // The org default (Statsig) — kept only for model-builder mode, which
  // resolves the objective client-side. Undefined in proposal mode, where
  // the proposal carries the server-resolved objective per sleeve.
  const defaultTerms = objectiveConfig?.buy_objective_terms
  const fallbackObjectiveTokens: EquationToken[] = defaultTerms?.length
    ? objectiveConfigToTokens(defaultTerms)
    : []

  // Create a sleeve for each account
  accountIds.forEach((accountId) => {
    const account = proposal.accounts[accountId]
    const beforeSleeves = account?.before?.sleeves

    const accountCash = account?.before?.cash
    const firstLotCurrency =
      (accountCash?.[0]?.currency as Currency) || Currency.USD

    // Each account has sleeves, we need to process each one
    // For proposal builder V2, we're creating new sleeves based on the account structure
    Object.entries(beforeSleeves || {}).forEach(([sleeveId, sleeveData]) => {
      const minAllocation = sleeveData.min_allocation
      const targetAllocation = sleeveData.target_allocation
      const maxAllocation = sleeveData.max_allocation

      const modelAssignment = sleeveData.model_assignment
      const resolvedObjective = sleeveResolvedObjective(sleeveData)

      // Currency is the sleeve's own: its cash lot's, falling back to the first
      // lot's. A model's currency constraint outranks both (below).
      const sleeveCurrency =
        (accountCash?.find((c) => c.sleeve_id === sleeveId)
          ?.currency as Currency) || firstLotCurrency
      const globalConstraints = createDefaultGlobalConstraints(sleeveCurrency)

      if (!modelAssignment) {
        // No model assignment, create sleeve with empty diversification parameter constraints (defaults)
        sleeves.push({
          sleeveId: sleeveId, // Use the original sleeve ID from account.before.sleeves
          name: sleeveData.name,
          minAllocation,
          targetAllocation,
          maxAllocation,
          state: {
            diversificationParameterConstraints:
              createEmptyDiversificationParameterConstraints(),
            riskFactors: {},
            cashflowTargets: [],
            cashflowTargetPriority: DEFAULT_CASHFLOW_TARGET_PRIORITY,
            rebalanceStrategy: toRebalanceOptions(undefined),
            otherConstraints: [],
            objectiveTokens: resolveSleeveObjectiveTokens({
              resolvedObjective,
              model: undefined,
              defaultTerms,
            }),
            objectiveSource: resolvedObjective?.source,
            globalConstraints,
            cashBuffer: 0,
            minimum_order_increment: sleeveData.minimum_order_increment ?? null,
          },
        })
        return
      }

      // Get the model from proposal.models. The transformer helpers below are
      // typed against the hand-written model shape (constraints family not yet
      // migrated to generated Constraint); bridge the generated proposal Model
      // to it here until that migration lands.
      const model = proposal.models?.[modelAssignment.model_id] as
        | Model
        | undefined

      if (!model?.strategy?.constraints) {
        // Model not found or has no strategy/constraints (e.g. auto-created empty model),
        // create sleeve with empty diversification parameter constraints (defaults)
        sleeves.push({
          sleeveId: sleeveId, // Use the original sleeve ID from account.before.sleeves
          name: sleeveData.name,
          modelId: modelAssignment?.model_id,
          modelName: modelAssignment?.model_name,
          minAllocation,
          targetAllocation,
          maxAllocation,
          state: {
            diversificationParameterConstraints:
              createEmptyDiversificationParameterConstraints(),
            riskFactors: {},
            cashflowTargets: modelAssignment.cashflow_targets || [],
            cashflowTargetPriority:
              parsePriority(modelAssignment.cashflow_target_priority) ??
              DEFAULT_CASHFLOW_TARGET_PRIORITY,
            rebalanceStrategy: toRebalanceOptions(
              modelAssignment.rebalance_strategy,
            ),
            assignmentDate: modelAssignment.assignment_date,
            otherConstraints: [],
            objectiveTokens: resolveSleeveObjectiveTokens({
              resolvedObjective,
              model,
              defaultTerms,
            }),
            objectiveStrategy: model?.objective_strategy,
            objectiveSource: resolvedObjective?.source,
            globalConstraints,
            cashBuffer:
              modelAssignment.cash_buffer_override ??
              model?.strategy?.cash_buffer ??
              0,
            minimum_order_increment: sleeveData.minimum_order_increment ?? null,
            modelMinimumOrderIncrement:
              model?.strategy?.minimum_order_increment ?? null,
            scopeRelativeWeights: model?.strategy?.scope_relative_weights,
          },
        })
        return
      }

      // Extract diversification parameter constraints for THIS sleeve from its model's constraints
      const {
        diversificationParameterConstraints,
        diversificationParameterConstraintIds,
      } = extractDiversificationParameterConstraints(model.strategy.constraints)

      // Merge model strategy with customizations, filtering out global and diversification constraints
      const globalConstraintIds = new Set(
        filterGlobalConstraints(model.strategy.constraints),
      )
      const filteredConstraintIds = new Set([
        ...diversificationParameterConstraintIds,
        ...globalConstraintIds,
      ])
      // FI constraints live under the container's fixed_income section
      const mergedConstraints = mergeStrategyWithCustomizations(
        model.strategy,
        fiConstraintsFromCustomizations(modelAssignment.customizations),
        filteredConstraintIds,
      )

      // A currency-only universe rule outside the system ids carries the
      // sleeve's currency, not a real rule: fold it into the global currency
      // constraint (edited at the headline) instead of letting it render as an
      // "other rule".
      const strayCurrency = mergedConstraints.reduce(
        (found, constraint) => found ?? readCurrencyConstraint(constraint),
        null as Currency | null,
      )
      const ruleConstraints = mergedConstraints.filter(
        (constraint) => readCurrencyConstraint(constraint) === null,
      )

      // Extract currency from the model's actual global-currency-constraint
      // so that currency changes made by minion are respected on reload; a
      // stray currency rule outranks the cash lot it was saved alongside.
      const effectiveCurrency =
        extractCurrencyFromConstraints(model.strategy?.constraints) ??
        strayCurrency ??
        sleeveCurrency
      const sleeveGlobalConstraints =
        createDefaultGlobalConstraints(effectiveCurrency)

      // Transform merged constraints to risk factors
      const { riskFactors, otherConstraints, ladderPolicy } =
        initStrategyToRiskFactors(
          consolidateUniverseConstraints(ruleConstraints),
        )

      // Extract cashflow targets from model assignment
      // No transformation needed - model_assignment.cashflow_targets is already CashflowTarget[]
      // (types/api/accounts.ts:537-545) which matches SleeveState.cashflowTargets
      const cashflowTargets = modelAssignment.cashflow_targets || []
      const cashflowTargetPriority =
        parsePriority(modelAssignment.cashflow_target_priority) ??
        DEFAULT_CASHFLOW_TARGET_PRIORITY

      const objectiveTokens = resolveSleeveObjectiveTokens({
        resolvedObjective,
        model,
        defaultTerms,
      })

      sleeves.push({
        sleeveId: sleeveId, // Use the original sleeve ID from account.before.sleeves
        name: sleeveData.name,
        modelId: modelAssignment?.model_id,
        modelName: modelAssignment?.model_name || model?.name,
        minAllocation,
        targetAllocation,
        maxAllocation,
        state: {
          diversificationParameterConstraints,
          riskFactors,
          cashflowTargets,
          cashflowTargetPriority,
          rebalanceStrategy: toRebalanceOptions(
            modelAssignment.rebalance_strategy,
          ),
          assignmentDate: modelAssignment.assignment_date,
          otherConstraints,
          ladderPolicy,
          objectiveTokens,
          objectiveStrategy: model.objective_strategy,
          objectiveSource: resolvedObjective?.source,
          globalConstraints: sleeveGlobalConstraints,
          cashBuffer:
            modelAssignment.cash_buffer_override ??
            model.strategy.cash_buffer ??
            0,
          minimum_order_increment: sleeveData.minimum_order_increment ?? null,
          modelMinimumOrderIncrement:
            model.strategy.minimum_order_increment ?? null,
          scopeRelativeWeights: model.strategy.scope_relative_weights,
        },
      })
    })
  })

  // If no sleeves created, create one empty sleeve
  if (sleeves.length === 0) {
    const fallbackSleeveId = generateId()
    sleeves.push({
      sleeveId: fallbackSleeveId,
      name: fallbackSleeveId, // Default name is the sleeve ID
      state: {
        diversificationParameterConstraints:
          createEmptyDiversificationParameterConstraints(),
        riskFactors: {},
        cashflowTargets: [],
        cashflowTargetPriority: DEFAULT_CASHFLOW_TARGET_PRIORITY,
        rebalanceStrategy: toRebalanceOptions(undefined),
        otherConstraints: [],
        objectiveTokens: fallbackObjectiveTokens,
        globalConstraints: createDefaultGlobalConstraints(),
        cashBuffer: 0,
        minimum_order_increment: null,
      },
      minAllocation: 0,
      targetAllocation: 0, // Market value in dollars, will be set from API or user input
      maxAllocation: 0,
    })
    sleeveCompliance[fallbackSleeveId] = {
      before: [],
      after: [],
    }
  }

  // Extract accountBefore / accountAfter snapshots from the first account.
  const firstAccountId = accountIds[0]
  let accountBefore = firstAccountId
    ? proposal.accounts[firstAccountId]?.before || null
    : null
  const accountAfter = firstAccountId
    ? proposal.accounts[firstAccountId]?.after || null
    : null
  accountBefore = cashWithSleeveFallback(accountBefore, sleeves)
  // Each lot follows its own sleeve's currency, so a currency change made by
  // minion is respected on reload and mixed-currency sleeves survive it.
  accountBefore = syncCashCurrencyToSleeves(accountBefore, sleeves)

  const raw = proposal.metadata
  const lastOptRiskGroupId =
    typeof raw?.last_optimization_risk_group_id === 'string'
      ? raw.last_optimization_risk_group_id
      : undefined
  const metadata: ProposalMetadata = {
    source:
      raw?.source === 'proposal_builder' || raw?.source === 'model_builder'
        ? raw.source
        : undefined,
    flags: {},
    last_optimization_risk_group_id: lastOptRiskGroupId,
    mode:
      raw?.mode === ProposalMode.Rebalancing ||
      raw?.mode === ProposalMode.NewMoney
        ? raw.mode
        : undefined,
    initial_account_cash: parseInitialAccountCash(raw?.initial_account_cash),
  }

  const activeAccount = accountIds[0] || ''
  const { holdingsBefore, holdingsAfter, holdingsHeld } =
    computeInitialHoldings(proposal, activeAccount)

  return {
    activeAccount,
    sleeves,
    orders: proposal.orders ?? [],
    proposalId: proposal.proposal_id,
    proposalName: proposal.name,
    isAccountLinked: proposal.synthetic === false,
    optimizationId: proposal.optimization_id ?? null,
    version: proposal.version ?? 1,
    proposalStatus: proposal.proposal_status,
    accountBefore,
    accountAfter,
    holdingsBefore,
    holdingsAfter,
    holdingsHeld,
    proposalActions: proposal.proposal_actions ?? [],
    sleeveStatistics,
    accountStatistics,
    sleeveCompliance,
    constraintRelaxation: proposal.solver_metrics?.constraint_relaxation ?? {},
    metadata,
    valuationBasis: proposal.valuation_config?.basis ?? undefined,
    flags: {
      dirtyCount: 0,
    },
  }
}
